<template>
  <div id="app" :class="[skinClasses]" class="h-100">
    <vue-progress-bar />

    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, computed } from '@vue/composition-api'

import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import { getUserRights } from '@/auth/utils'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    // Set a global chat ping to display any changes in the menu
    const MESSAGE_FETCH_RATE = 3 * 60 * 1000 // 3 minutes

    const rights = computed(getUserRights)

    const fetchChatAndContacts = () => {
      const userData = store.getters['app-common/userData']

      if (userData.id && rights && rights.value.includes('Chats_Chat_View')) {
        store.dispatch('app-chat/fetchChatsAndContacts').then(response => {
          let sum = 0

          response.data.map(c => {
            if (c.unreadMsgsCnt) {
              sum += parseInt(c.unreadMsgsCnt, 10)
            }
            return c
          })

          store.commit('app-chat/unreadNotifications', sum)
        })
      }
    }
//to get count of new partner requests created and accepted
    const fetchNewRequests = () => {
      const userData = store.getters['app-common/userData']

      if (userData.id && rights && rights.value.includes('Users_Partner_Connect_Edit')) {
        store.dispatch('app-chat/fetchNewRequests').then(response => {

          let count = 0
          count += response.data.count
          store.commit('app-chat/unreadRequests', count)

        })
      }
    }

    // Arbitrary timeout to init the user state first -if exists-
    setTimeout(() => {
      fetchChatAndContacts()
      fetchNewRequests()
    }, 5 * 1000)

    setInterval(() => {
      fetchChatAndContacts()
      fetchNewRequests()
    }, MESSAGE_FETCH_RATE)

    return {
      skinClasses,
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2),
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/quill.scss';
</style>
